import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  noindex?: boolean;
}

export function SEO({ title, description, image, noindex = false }: SEOProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentLanguage = i18n.language.startsWith('en') ? 'en' : 'id';
  const baseUrl = window?.location?.origin || import.meta.env.FRONTEND_URL;

  const defaultTitle = 'Kaizen Network - Minecraft Server Indonesia';
  const defaultDescription = t('seo.description', 'Join Kaizen Network, the best Indonesian Minecraft server featuring Survival Mix, SkyBlock Tycoon, and Creative Mode. Play now with thousands of players!');
  const defaultImage = `${baseUrl}/images/kaizen-network-white-logo.svg`;

  const seoTitle = title || defaultTitle;
  const seoDescription = description || defaultDescription;
  const seoImage = image || defaultImage;
  
  // Get the path without language prefix
  const pathWithoutLang = location.pathname.replace(/^\/(en|id)/, '');
  
  // Generate URLs for each language
  const enUrl = `${baseUrl}/en${pathWithoutLang}`;
  const idUrl = `${baseUrl}/id${pathWithoutLang}`;
  
  // Set canonical URL to the current language version
  const canonicalUrl = currentLanguage === 'en' ? enUrl : idUrl;
  
  // Set x-default to the root URL + path without language
  const xDefaultUrl = `${baseUrl}${pathWithoutLang}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLanguage} />
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content="minecraft, minecraft server, minecraft indonesia, minecraft server indonesia, kaizen network, minecraft indonesia, survival, skyblock, creative" />
      
      {/* Canonical URL - Always points to the current language version */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots Meta Tag */}
      <meta 
        name="robots" 
        content={noindex ? 'noindex,nofollow' : 'index,follow'} 
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Kaizen Network" />
      <meta property="og:locale" content={currentLanguage === 'en' ? 'en_US' : 'id_ID'} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />

      {/* Language Alternates */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="id" href={idUrl} />
      <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Kaizen Network',
          url: baseUrl,
          logo: `${baseUrl}/images/kaizen-network-white-logo.svg`,
          description: seoDescription,
          sameAs: [
            'https://dsc.gg/kaizenmc',
            'https://www.tiktok.com/@kaizenetwork'
          ]
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Kaizen Network',
          url: baseUrl,
          potentialAction: {
            '@type': 'PlayAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: 'minecraft://connect/play.kaizenmc.id'
            }
          }
        })}
      </script>
    </Helmet>
  );
} 